import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Youtube, Download, Image, Play, AlertCircle, ExternalLink } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';
import { youtubeService, downloadService } from '../services/socialMediaApi';

interface ThumbnailData {
  videoId: string;
  title: string;
  channel: string;
  duration: string;
  views: string;
  uploadDate: string;
  thumbnails: {
    quality: string;
    url: string;
    width: number;
    height: number;
  }[];
}

const YouTubeThumbnailDownloader: React.FC = () => {
  const [videoUrl, setVideoUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [thumbnailData, setThumbnailData] = useState<ThumbnailData | null>(null);

  useSEO({
    title: 'YouTube Thumbnail Downloader - Download YT Video Thumbnails',
    description: 'Download YouTube video thumbnails in all available qualities. Free tool to save YouTube thumbnails in HD, SD, and other resolutions.',
    keywords: 'youtube thumbnail downloader, yt thumbnail download, youtube thumbnail saver, video thumbnail extractor',
    canonical: 'https://snap-tools.online/social/youtube-thumbnail-downloader'
  });

  // Fetch video data
  const fetchVideoData = async () => {
    if (!videoUrl.trim()) {
      toast.error('Please enter a YouTube URL or video ID');
      return;
    }

    const videoId = youtubeService.extractVideoId(videoUrl.trim());
    if (!videoId) {
      toast.error('Invalid YouTube URL or video ID');
      return;
    }

    setIsLoading(true);
    try {
      // Get video information using YouTube oEmbed API (real data)
      const videoInfo = await youtubeService.getVideoInfo(videoId);
      
      // Get all thumbnail qualities using real YouTube thumbnail URLs
      const thumbnails = youtubeService.getThumbnails(videoId);

      // Validate that thumbnails exist
      const validatedThumbnails = await Promise.all(
        thumbnails.map(async (thumbnail) => {
          const isValid = await downloadService.validateImageUrl(thumbnail.url);
          return { ...thumbnail, isValid };
        })
      );

      const thumbnailData: ThumbnailData = {
        videoId,
        title: videoInfo.title,
        channel: videoInfo.author,
        duration: 'N/A', // Duration not available in oEmbed
        views: 'N/A', // Views not available in oEmbed
        uploadDate: 'N/A', // Upload date not available in oEmbed
        thumbnails: validatedThumbnails
      };

      setThumbnailData(thumbnailData);
      toast.success('Video thumbnails loaded successfully!');
    } catch (error) {
      console.error('Failed to fetch video data:', error);
      toast.error('Failed to load video data. Please check the URL and ensure the video is public.');
    } finally {
      setIsLoading(false);
    }
  };

  // Download all thumbnails
  const downloadAllThumbnails = async () => {
    if (!thumbnailData) return;

    toast('Starting batch download...');
    let successful = 0;
    let failed = 0;

    for (const thumbnail of thumbnailData.thumbnails) {
      try {
        await downloadThumbnail(thumbnail);
        successful++;
        // Small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        failed++;
        console.error(`Failed to download ${thumbnail.quality}:`, error);
      }
    }

    if (successful > 0 && failed === 0) {
      toast.success(`Successfully downloaded all ${successful} thumbnails!`);
    } else if (successful > 0) {
      toast.success(`Downloaded ${successful} thumbnails. ${failed} failed.`);
    } else {
      toast.error('Failed to download thumbnails');
    }
  };

  // Download thumbnail
  const downloadThumbnail = async (thumbnail: ThumbnailData['thumbnails'][0]) => {
    try {
      const filename = `${thumbnailData?.videoId}_${thumbnail.quality.replace(/[^a-zA-Z0-9]/g, '_')}.jpg`;
      
      // Use the real download service
      const success = await downloadService.downloadFile(thumbnail.url, filename, { useProxy: true });
      
      if (success) {
        toast.success('Download completed successfully!');
      } else {
        toast.error('Download failed. The thumbnail may not be available or accessible.');
      }
    } catch (error) {
      console.error('Download failed:', error);
      
      // Fallback method
      try {
        const link = document.createElement('a');
        link.href = thumbnail.url;
        link.download = `${thumbnailData?.videoId}_${thumbnail.quality.replace(/[^a-zA-Z0-9]/g, '_')}.jpg`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success('Download initiated! If blocked by browser, right-click the thumbnail and select "Save image as..."');
      } catch (fallbackError) {
        toast.error('Download failed. Please right-click the thumbnail and select "Save image as..." to download manually.');
      }
    }
  };

  // Load example
  const loadExample = () => {
    setVideoUrl('https://www.youtube.com/watch?v=dQw4w9WgXcQ');
    toast.success('Example URL loaded');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
              <Youtube className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">YouTube Thumbnail Downloader</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Download YouTube video thumbnails in all available qualities. Get high-resolution thumbnails for any YouTube video.
            </p>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          {/* Input Form */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  YouTube Video URL or Video ID
                </label>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=VIDEO_ID or just VIDEO_ID"
                    className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    onKeyPress={(e) => e.key === 'Enter' && fetchVideoData()}
                  />
                  <button
                    onClick={loadExample}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Example
                  </button>
                </div>
              </div>

              <button
                onClick={fetchVideoData}
                disabled={isLoading || !videoUrl.trim()}
                className="w-full bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>Loading...</>
                ) : (
                  <>
                    <Image className="w-4 h-4" />
                    Get Thumbnails
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Video Info */}
          {thumbnailData && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white border border-gray-200 rounded-lg p-6 mb-8"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="relative">
                  <img
                    src={thumbnailData.thumbnails[0].url}
                    alt="Video thumbnail"
                    className="w-32 h-24 object-cover rounded-lg"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded-lg">
                    <Play className="w-8 h-8 text-white" />
                  </div>
                </div>
                
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{thumbnailData.title}</h3>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p>Channel: {thumbnailData.channel}</p>
                    <p>Duration: {thumbnailData.duration}</p>
                    <p>{thumbnailData.views} • {thumbnailData.uploadDate}</p>
                    <p>Video ID: {thumbnailData.videoId}</p>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <button
                    onClick={downloadAllThumbnails}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download All
                  </button>
                  <button
                    onClick={() => window.open(`https://www.youtube.com/watch?v=${thumbnailData.videoId}`, '_blank')}
                    className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View Video
                  </button>
                </div>
              </div>

              {/* Thumbnails Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {thumbnailData.thumbnails.map((thumbnail, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4">
                    <div className="relative mb-3">
                      <img
                        src={thumbnail.url}
                        alt={`Thumbnail ${thumbnail.quality}`}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                      <div className="absolute top-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                        {thumbnail.width}x{thumbnail.height}
                      </div>
                    </div>
                    
                    <div className="text-center">
                      <h4 className="font-medium text-gray-900 mb-2">{thumbnail.quality}</h4>
                      <p className="text-sm text-gray-600 mb-3">
                        {thumbnail.width} × {thumbnail.height} pixels
                      </p>
                      <button
                        onClick={() => downloadThumbnail(thumbnail)}
                        className="w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Important Notice */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-yellow-900 mb-1">Usage Guidelines</h4>
                <p className="text-sm text-yellow-800">
                  Downloaded thumbnails are for personal use only. Please respect YouTube's terms of service and 
                  the content creator's rights. Always obtain permission before using thumbnails commercially.
                </p>
              </div>
            </div>
          </div>

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mb-6"
          />

          {/* Educational Content */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-3">How to Use</h4>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• Copy the YouTube video URL from your browser</li>
                <li>• Paste it into the input field above</li>
                <li>• Click "Get Thumbnails" to load all available sizes</li>
                <li>• Choose the quality you want to download</li>
                <li>• Click "Download" or "Download All" to save</li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-lg p-6">
              <h4 className="font-semibold text-green-900 mb-3">Supported Formats</h4>
              <ul className="text-sm text-green-800 space-y-2">
                <li>• Standard YouTube URLs (youtube.com/watch)</li>
                <li>• Short URLs (youtu.be/VIDEO_ID)</li>
                <li>• YouTube Shorts URLs</li>
                <li>• Embed URLs (youtube.com/embed)</li>
                <li>• Direct video IDs (11-character strings)</li>
              </ul>
            </div>

            <div className="bg-purple-50 rounded-lg p-6">
              <h4 className="font-semibold text-purple-900 mb-3">Available Qualities</h4>
              <ul className="text-sm text-purple-800 space-y-2">
                <li>• <strong>Maximum:</strong> 1280x720 (HD quality)</li>
                <li>• <strong>High:</strong> 480x360 (good quality)</li>
                <li>• <strong>Medium:</strong> 320x180 (standard)</li>
                <li>• <strong>Standard:</strong> 120x90 (small)</li>
                <li>• <strong>Default:</strong> 120x90 (thumbnail)</li>
              </ul>
            </div>

            <div className="bg-red-50 rounded-lg p-6">
              <h4 className="font-semibold text-red-900 mb-3">Tips & Tricks</h4>
              <ul className="text-sm text-red-800 space-y-2">
                <li>• Maximum quality may not be available for all videos</li>
                <li>• Older videos might have limited quality options</li>
                <li>• Private or deleted videos won't work</li>
                <li>• Age-restricted videos may have limitations</li>
                <li>• Always respect copyright and fair use policies</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default YouTubeThumbnailDownloader;
